// 환자 상세 「서류」 카드 (P2-12 서류함, 2026-09-14) — 치과가 발급한 서류 사본을 올리면 환자가 덴클 앱 「서류」에서 받는다.
// 근거: docs/기획_치과환자관리_전체지도_2026-09-11.md §5-2-12 · docs/법무보완_서류함_2026-09-14.md
// 흐름: 종류 선택 → 파일 선택(PDF·JPEG·PNG, 20MB) → 확인 모달(환자 이름·뒤4자리는 서버 응답값) → 서명 URL PUT(진행률) → finalize.
// 권한: 올리기 = 오너·직원 / 삭제 = 오너만(사유 select). 서류 수신 꺼짐이면 올리기 차단(법무보완 §3-3).
// 2026-09-16 환자기능확장: 수신 여부 = 서버 record.docs(cp-1.4 docsPref 우선, 없으면 cp-1.2 consents.docs).
//   기존(cp-1.1~1.2) 미동의 환자에게만 [앱으로 서류 받기 요청](requestPatientDocsConsent) — 문구 정본 cp-1.3 §5-4 · cp-1.4 §4-2.
// 워터마크 없음(대표 확정). 혜택·무료·할인 문구 없음(의료법). 서버 계약은 clinicPatientsApi.js 상단 D-1~D-7 가정.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Upload, ExternalLink, Trash2, Send } from 'lucide-react';
import { unifiedStyles as U } from '../../styles/Styles';
import {
    DOC_TYPES, DOC_DELETE_REASONS, DOCS_CONSENT_REQUIRED, DOCS_COPY_NOTICE, docTypeLabel, validateDocFile,
    issueDocUploadUrl, uploadDocToSignedUrl, finalizeClinicPatientDoc, listClinicPatientDocs, issueDocDownloadUrl,
    deleteClinicPatientDoc, describeError, isDocsConsentRequired,
    DOCS_PREF_OFF, DOCS_REQUEST_TEXT, requestPatientDocsConsent, describeDocsRequestError,
} from '../../services/clinicPatientsApi';
import { effectiveDocs } from '../../config/consentVersions';
import { getFeatures } from '../../utils/features';

const fmt = (v) => {
    if (!v) return '';
    const d = typeof v === 'number' ? new Date(v) : (v._seconds ? new Date(v._seconds * 1000) : (v.toDate ? v.toDate() : new Date(v)));
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};
const fmtSize = (n) => {
    if (!n && n !== 0) return '';
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
    return `${(n / 1024 / 1024).toFixed(1)}MB`;
};
const mmdd = (ms) => { if (!ms) return ''; const d = new Date(ms); return Number.isNaN(d.getTime()) ? '' : `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`; };
// 요청 결과 안내 — cp-1.4 정본 부록 A-2 공통안. 환자 알림 설정·기기 상태는 치과에 노출하지 않는다(원인은 콘솔·서버 로그로만)
const PUSH_SENT_TEXT = '요청을 보냈습니다. 환자가 앱에서 서류 받기를 선택한 경우에만 서류를 올릴 수 있습니다.';
const PUSH_NOT_SENT_TEXT = '요청을 기록했습니다. 환자가 앱을 열면 요청이 표시됩니다.';
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// props: pid, record(getClinicPatientDetail record — docs·docsPref·docsConsentRequest 판독), user, isOwner, initialDocs(상세 응답에 docs[]가 실리면 전달)
function ClinicPatientDocs({ pid, record, user, isOwner, initialDocs }) {
    const [docs, setDocs] = useState(Array.isArray(initialDocs) ? initialDocs : []);
    const [loading, setLoading] = useState(!Array.isArray(initialDocs));
    const [listError, setListError] = useState('');
    const [diagnosisOn, setDiagnosisOn] = useState(false);

    // 올리기 상태
    const [docType, setDocType] = useState('receipt');
    const [issuedAt, setIssuedAt] = useState(todayStr());
    const [file, setFile] = useState(null);
    const [formError, setFormError] = useState('');
    const [confirm, setConfirm] = useState(null);       // { docId, uploadUrl, method, headers, patientName, phoneLast4 }
    const [busy, setBusy] = useState('');               // 'issue' | 'upload' | 'finalize'
    const [progress, setProgress] = useState(0);
    const [uploadError, setUploadError] = useState('');
    const fileRef = useRef(null);

    // 열기·삭제 상태
    const [rowBusy, setRowBusy] = useState('');
    const [rowError, setRowError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteReason, setDeleteReason] = useState('misupload');

    // 서류 받기 요청 상태 — 서버 docsConsentRequest(상세 응답). 요청 성공·거부 응답 후에는 로컬 값으로 덮어 즉시 반영
    const [reqOverride, setReqOverride] = useState(null);
    const [reqConfirm, setReqConfirm] = useState(false);
    const [reqBusy, setReqBusy] = useState(false);
    const [reqError, setReqError] = useState('');
    const [reqDone, setReqDone] = useState('');
    const req = reqOverride || record?.docsConsentRequest || null;

    // 서버 값(record.docs) 우선 — 구 서버 응답(docs 필드 없음)은 같은 규칙으로 판정
    const consentOk = typeof record?.docs === 'boolean' ? record.docs : effectiveDocs(record);
    const isPrefRecord = !!record?.docsPref;             // cp-1.4 레코드(서류 기본 켜짐) — 요청 버튼 없음
    const isDeleted = record?.status === 'deleted';
    const canUpload = consentOk && !isDeleted;

    // 미수신 상태 안내: { text, subText?, showButton, buttonEnabled }
    const docsGate = (() => {
        if (consentOk || isDeleted) return null;
        if (isPrefRecord || req?.blockedReason === 'docs-pref-off') return { text: DOCS_PREF_OFF, showButton: false };
        if (!req) return { text: DOCS_CONSENT_REQUIRED, showButton: false };   // 서버 미배포(요청 상태 없음) — 종전 안내
        const declined = req.response === 'declined' && req.respondedAt && (!req.lastRequestedAt || req.respondedAt >= req.lastRequestedAt);
        const declinedText = declined ? `환자가 ${mmdd(req.respondedAt)} 서류 받기를 선택하지 않았습니다.` : '';
        switch (req.blockedReason) {
            case 'docs-consent-revoked': return { text: DOCS_REQUEST_TEXT.revoked, showButton: true, buttonEnabled: false };
            case 'patient-app-not-registered': return { text: DOCS_REQUEST_TEXT.notLinked, showButton: true, buttonEnabled: false };
            case 'record-not-active': return { text: record?.status === 'deleteRequested' ? DOCS_REQUEST_TEXT.notActiveDeleteRequested : record?.status === 'deleted' ? DOCS_REQUEST_TEXT.notActiveDeleted : DOCS_REQUEST_TEXT.notActive, showButton: true, buttonEnabled: false };
            case 'docs-request-declined-limit': return { text: DOCS_REQUEST_TEXT.declinedLimit, showButton: true, buttonEnabled: false };
            case 'docs-request-too-soon':
                return declined
                    ? { text: declinedText, subText: DOCS_REQUEST_TEXT.intervalNote, showButton: true, buttonEnabled: false }
                    : { text: `${mmdd(req.lastRequestedAt)}에 요청했습니다. ${DOCS_REQUEST_TEXT.intervalNote}`, showButton: true, buttonEnabled: false };
            case 'docs-already-agreed': return null;   // 응답 시점 차이 — 새로고침하면 올리기 활성
            default:
                if (req.canRequest) {
                    return declined
                        ? { text: declinedText, subText: DOCS_REQUEST_TEXT.canRequest, showButton: true, buttonEnabled: true }
                        : { text: DOCS_REQUEST_TEXT.canRequest, subText: req.lastRequestedAt ? `마지막 요청: ${mmdd(req.lastRequestedAt)}` : '', showButton: true, buttonEnabled: true };
                }
                return { text: DOCS_CONSENT_REQUIRED, showButton: false };
        }
    })();

    const sendDocsRequest = async () => {
        setReqBusy(true); setReqError(''); setReqDone('');
        try {
            const res = await requestPatientDocsConsent(pid);
            const result = res?.push?.result || 'failed';
            if (result !== 'sent') console.error('[ClinicPatientDocs] 서류 받기 요청 알림 미발송(요청은 기록됨) result=', result);
            setReqOverride({ ...(req || {}), lastRequestedAt: res?.requestedAt || Date.now(), nextAvailableAt: res?.nextAvailableAt || null,
                count: Number(req?.count || 0) + 1, response: null, respondedAt: null, canRequest: false, blockedReason: 'docs-request-too-soon' });
            setReqDone(result === 'sent' ? PUSH_SENT_TEXT : PUSH_NOT_SENT_TEXT);
            setReqConfirm(false);
        } catch (e) {
            // 서버가 거부 사유를 주면 화면 상태도 그 사유로 맞춘다(다른 직원이 먼저 보낸 경우 등)
            const code = e?.code === 'functions/failed-precondition' ? e?.details?.code : null;
            if (code) {
                setReqOverride({ ...(req || {}), canRequest: false, blockedReason: code,
                    ...(code === 'docs-request-too-soon' && e?.details?.nextAvailableAt ? { lastRequestedAt: e.details.nextAvailableAt - 30 * 24 * 3600 * 1000, nextAvailableAt: e.details.nextAvailableAt } : {}) });
            }
            setReqError(describeDocsRequestError(e));
            setReqConfirm(false);
        } finally { setReqBusy(false); }
    };

    const load = useCallback(async () => {
        setLoading(true); setListError('');
        try {
            const res = await listClinicPatientDocs(pid);
            setDocs(Array.isArray(res?.docs) ? res.docs : []);
        } catch (e) {
            setListError(describeError(e, '서류 목록을 불러오지 못했습니다.'));
        } finally { setLoading(false); }
    }, [pid]);

    useEffect(() => {
        // 상세 응답에 docs[]가 실려 있으면 그대로 쓰고(계약 D-4 주석), 없을 때만 목록 CF 호출
        if (!Array.isArray(initialDocs)) load();
        else setDocs(initialDocs);
    }, [initialDocs, load]);

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setDiagnosisOn(f?.docsDiagnosisEnabled === true); })
            .catch(e => console.error('[ClinicPatientDocs] features 읽기 실패', e));
        return () => { alive = false; };
    }, []);

    const typeOptions = DOC_TYPES.filter(t => !t.flagged || diagnosisOn);

    const onPickFile = (e) => {
        const f = e.target.files?.[0] || null;
        setFile(f); setFormError(f ? validateDocFile(f) : '');
    };

    // 1단계: 서명 URL 발급 → 확인 모달 (환자 이름은 서버 응답값)
    const startUpload = async () => {
        if (!canUpload) { setFormError(consentOk ? '삭제된 환자에게는 올릴 수 없습니다.' : (docsGate?.text || DOCS_CONSENT_REQUIRED)); return; }
        const v = validateDocFile(file);
        if (v) { setFormError(v); return; }
        if (!typeOptions.some(t => t.value === docType)) { setFormError('서류 종류를 선택해 주세요.'); return; }
        setBusy('issue'); setFormError(''); setUploadError('');
        try {
            const res = await issueDocUploadUrl({ pid, type: docType, contentType: file.type, size: file.size, issuedAt });
            if (!res?.docId || !res?.uploadUrl) throw new Error('업로드 주소를 받지 못했습니다.');
            setConfirm(res);
        } catch (e) {
            setFormError(isDocsConsentRequired(e) ? DOCS_CONSENT_REQUIRED : describeError(e, e?.message || '업로드를 시작하지 못했습니다.'));
        } finally { setBusy(''); }
    };

    // 2단계: 확인 후 PUT 업로드 → finalize
    const doUpload = async () => {
        if (!confirm || !file) return;
        setBusy('upload'); setProgress(0); setUploadError('');
        try {
            await uploadDocToSignedUrl({ uploadUrl: confirm.uploadUrl, method: confirm.method || 'PUT', headers: confirm.headers, file, onProgress: setProgress });
            setBusy('finalize');
            await finalizeClinicPatientDoc(pid, confirm.docId);
            setConfirm(null); setFile(null); if (fileRef.current) fileRef.current.value = '';
            await load();
        } catch (e) {
            // 업로드는 됐는데 finalize가 실패하면 메타 없는 객체가 남는다 — 서버 24h 고아 정리(§5-2-12 DoD)에 맡기되 화면에는 반드시 알린다
            setUploadError(describeError(e, e?.message || '업로드에 실패했습니다.'));
        } finally { setBusy(''); }
    };

    const openDoc = async (d) => {
        setRowBusy(d.docId); setRowError('');
        try {
            const res = await issueDocDownloadUrl(pid, d.docId);
            if (!res?.url) throw new Error('열기 주소를 받지 못했습니다.');
            const w = window.open(res.url, '_blank', 'noopener');
            if (!w) setRowError('팝업이 차단되었습니다. 브라우저 팝업 허용 후 다시 눌러 주세요.');
        } catch (e) { setRowError(describeError(e, e?.message || '서류를 열지 못했습니다.')); }
        finally { setRowBusy(''); }
    };

    const removeDoc = async () => {
        if (!isOwner || !deleteTarget) return;
        setRowBusy(deleteTarget.docId); setRowError('');
        try {
            await deleteClinicPatientDoc(pid, deleteTarget.docId, deleteReason);
            setDeleteTarget(null);
            await load();
        } catch (e) { setRowError(describeError(e, e?.message || '서류를 삭제하지 못했습니다.')); }
        finally { setRowBusy(''); }
    };

    const uploaderLabel = (d) => {
        if (d.uploadedBy && d.uploadedBy === user?.uid) return '나';
        if (d.uploadedRole === 'owner') return '대표';
        if (d.uploadedRole === 'staff') return `직원 ${String(d.uploadedBy || '').slice(0, 6)}`;
        return d.uploadedBy ? String(d.uploadedBy).slice(0, 6) : '';
    };

    const errBox = (msg) => msg ? <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{msg}</div> : null;

    return (
        <div style={{ ...U.card, marginBottom: 12 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}><FileText size={16} color="#6366f1" /> 서류</h3>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>치과가 발급한 진료비 영수증·세부내역서 등의 사본을 올리면 환자가 덴클 앱 「서류」에서 받아 봅니다.</div>
            {consentOk && !isDeleted && (
                <div style={{ fontSize: 12, color: '#047857', marginBottom: 8 }}>
                    {isPrefRecord ? '서류 사본 받기: 켜짐 (등록 시 민감정보 처리 동의에 포함)' : '서류 사본 받기: 켜짐 (환자 선택 동의)'}
                </div>
            )}

            {/* 서류 받기 요청 확인 모달 — 문구 정본 cp-1.3 §5-4 */}
            {reqConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ ...U.card, width: '100%', maxWidth: 420 }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>앱으로 서류 받기 요청</h3>
                        <div style={{ fontSize: 14, color: '#0f172a', marginBottom: 12, lineHeight: 1.6 }}>
                            이 환자에게 서류 받기 선택 요청을 보냅니다: <b>{record?.name || '(이름 없음)'}</b>{record?.birthDate ? ` (${String(record.birthDate).slice(0, 4)})` : ''}. 환자가 동의해야 서류를 올릴 수 있습니다.
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" disabled={reqBusy} onClick={() => setReqConfirm(false)} style={U.buttonSecondary}>취소</button>
                            <button type="button" disabled={reqBusy} onClick={sendDocsRequest} style={U.buttonPrimary}><Send size={14} /> {reqBusy ? '보내는 중...' : '보내기'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 확인 모달 — 환자 이름·뒤4자리는 issueDocUploadUrl 응답값 */}
            {confirm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ ...U.card, width: '100%', maxWidth: 440 }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>환자 확인</h3>
                        <div style={{ fontSize: 14, color: '#0f172a', marginBottom: 10, lineHeight: 1.6 }}>
                            이 서류를 <b>{confirm.patient?.name || confirm.patientName || '(이름 없음)'}</b> ({(confirm.patient?.phoneLast4 || confirm.phoneLast4) ? `***-${confirm.patient?.phoneLast4 || confirm.phoneLast4}` : '연락처 없음'}) 님에게 전달합니다.<br />
                            환자가 맞는지 확인해 주세요.
                        </div>
                        <div style={{ fontSize: 13, color: '#334155', background: '#f8fafc', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                            <div>종류: {docTypeLabel(docType)}</div>
                            <div>파일: {file?.name} ({fmtSize(file?.size)})</div>
                            <div>발급일: {issuedAt}</div>
                        </div>
                        {busy === 'upload' && (
                            <div style={{ marginBottom: 8 }}>
                                <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: `${progress}%`, height: '100%', background: '#6366f1', transition: 'width .2s' }} /></div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>올리는 중 {progress}%</div>
                            </div>
                        )}
                        {busy === 'finalize' && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>등록 확인 중...</div>}
                        {errBox(uploadError)}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" disabled={!!busy} onClick={() => { setConfirm(null); setUploadError(''); }} style={U.buttonSecondary}>취소</button>
                            <button type="button" disabled={!!busy} onClick={doUpload} style={U.buttonPrimary}><Upload size={14} /> {busy ? '올리는 중...' : '맞습니다, 올리기'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 삭제 모달 — 오너만 */}
            {deleteTarget && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ ...U.card, width: '100%', maxWidth: 420 }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>서류 삭제</h3>
                        <div style={{ fontSize: 13, color: '#334155', marginBottom: 10 }}>{docTypeLabel(deleteTarget.type)} ({fmt(deleteTarget.issuedAt || deleteTarget.createdAt)}) 사본이 즉시 지워지며 환자 앱에서도 사라집니다. 되돌릴 수 없습니다. 사유를 선택해 주세요.</div>
                        {DOC_DELETE_REASONS.map(r => (
                            <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: '#0f172a', cursor: 'pointer' }}>
                                <input type="radio" name="docDeleteReason" value={r.value} checked={deleteReason === r.value} onChange={() => setDeleteReason(r.value)} />
                                {r.label}
                            </label>
                        ))}
                        {errBox(rowError)}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                            <button type="button" disabled={rowBusy === deleteTarget.docId} onClick={() => { setDeleteTarget(null); setRowError(''); }} style={U.buttonSecondary}>취소</button>
                            <button type="button" disabled={rowBusy === deleteTarget.docId} onClick={removeDoc} style={{ ...U.buttonPrimary, background: '#dc2626' }}><Trash2 size={14} /> {rowBusy === deleteTarget.docId ? '삭제 중...' : '삭제'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 올리기 폼 — 오너·직원 */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 10, background: canUpload ? '#fff' : '#f8fafc' }}>
                {docsGate && (
                    <div style={{ fontSize: 13, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                        <div>{docsGate.text}</div>
                        {docsGate.subText && <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>{docsGate.subText}</div>}
                        {docsGate.showButton && (
                            <button type="button" disabled={!docsGate.buttonEnabled || reqBusy} onClick={() => { setReqError(''); setReqDone(''); setReqConfirm(true); }}
                                style={{ ...U.buttonSecondary, marginTop: 8, padding: '4px 12px', fontSize: 12, opacity: docsGate.buttonEnabled ? 1 : 0.5, cursor: docsGate.buttonEnabled ? 'pointer' : 'not-allowed' }}>
                                <Send size={12} /> 앱으로 서류 받기 요청
                            </button>
                        )}
                    </div>
                )}
                {reqDone && <div style={{ fontSize: 13, color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>{reqDone}</div>}
                {errBox(reqError)}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select value={docType} onChange={(e) => setDocType(e.target.value)} disabled={!canUpload || !!busy}
                        style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
                        {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                        발급일
                        <input type="date" value={issuedAt} max={todayStr()} onChange={(e) => setIssuedAt(e.target.value)} disabled={!canUpload || !!busy}
                            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8 }} />
                    </label>
                    <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" onChange={onPickFile} disabled={!canUpload || !!busy} style={{ fontSize: 12 }} />
                    <button type="button" disabled={!canUpload || !file || !!formError || !!busy} onClick={startUpload}
                        title={!consentOk ? (docsGate?.text || DOCS_CONSENT_REQUIRED) : ''} style={{ ...U.buttonPrimary, opacity: (!canUpload || !file || !!formError) ? 0.5 : 1 }}>
                        <Upload size={14} /> {busy === 'issue' ? '준비 중...' : '서류 올리기'}
                    </button>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>PDF·JPEG·PNG, 1건 20MB 이하. 파일명·표시명에 환자 이름을 넣지 마세요(서버에는 종류·발급일만 기록됩니다).</div>
                {formError && <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>{formError}</div>}
            </div>

            {/* 목록 */}
            {errBox(listError)}
            {rowError && !deleteTarget && errBox(rowError)}
            {loading ? (
                <div style={{ fontSize: 13, color: '#94a3b8' }}>서류 목록 불러오는 중...</div>
            ) : docs.length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8' }}>올린 서류가 없습니다.</div>
            ) : docs.map(d => (
                <div key={d.docId} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{docTypeLabel(d.type)}{d.issuedAt ? <span style={{ fontWeight: 400, color: '#64748b' }}> · 발급 {fmt(d.issuedAt)}</span> : null}</div>
                        <div style={{ color: '#64748b', fontSize: 12 }}>올린 날 {fmt(d.createdAt)}{d.size ? ` · ${fmtSize(d.size)}` : ''}{uploaderLabel(d) ? ` · ${uploaderLabel(d)}` : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" disabled={rowBusy === d.docId} onClick={() => openDoc(d)} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 }}><ExternalLink size={12} /> {rowBusy === d.docId ? '여는 중...' : '열기'}</button>
                        {isOwner && <button type="button" disabled={rowBusy === d.docId} onClick={() => { setDeleteReason('misupload'); setDeleteTarget(d); }} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, color: '#b91c1c', borderColor: '#fecaca' }}><Trash2 size={12} /> 삭제</button>}
                    </div>
                </div>
            ))}
            {!isOwner && docs.length > 0 && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>서류 삭제는 치과 대표만 할 수 있습니다.</div>}

            {/* 고정 문구 — 명세 §5-2-12·법무보완 §4. 문구 변경 시 법적보안 확인 */}
            <div style={{ fontSize: 12, color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', marginTop: 10, lineHeight: 1.5 }}>
                {DOCS_COPY_NOTICE}
            </div>
        </div>
    );
}

export default ClinicPatientDocs;
